import { resolveDynamicImageUrl } from './imageUrl';
import constructionBuildingCover from '../assets/images/manpro-covers/construction-building.png';
import tenderDocsCover from '../assets/images/manpro-covers/tender-docs.png';
import mepUtilityCover from '../assets/images/manpro-covers/mep-utility.png';
import bridgeInfraCover from '../assets/images/manpro-covers/bridge-infra.png';
import type {
  OwnerProjectDocumentDto,
  OwnerProjectGalleryImageDto,
  OwnerProjectMetadata,
  ProviderOpportunityDto,
} from '../api/manpro/types';

type OpportunityMediaSource = Pick<
  ProviderOpportunityDto,
  'gallery_images' | 'documents' | 'owner_project_documents' | 'category' | 'procurement_type' | 'title' | 'submission_type' | 'source_url'
> & {
  metadata?: Pick<OwnerProjectMetadata, 'gallery_images' | 'document_links'> | null;
};

type MediaCandidate = {
  keywords: string[];
  cover: string;
};

const fallbackCovers: MediaCandidate[] = [
  {
    keywords: ['gedung', 'kantor', 'konstruksi gedung', 'building'],
    cover: constructionBuildingCover,
  },
  {
    keywords: ['jalan', 'aspal', 'jembatan', 'sipil', 'infrastruktur'],
    cover: bridgeInfraCover,
  },
  {
    keywords: ['pabrik', 'industri', 'warehouse', 'gudang'],
    cover: constructionBuildingCover,
  },
  {
    keywords: ['rumah', 'renovasi', 'interior'],
    cover: tenderDocsCover,
  },
  {
    keywords: ['mep', 'elektrikal', 'listrik', 'panel', 'solar', 'hvac', 'mechanical'],
    cover: mepUtilityCover,
  },
  {
    keywords: ['subkontraktor', 'partisi', 'plafon', 'finishing', 'interior'],
    cover: tenderDocsCover,
  },
];

const fallbackDefaultCover =
  tenderDocsCover;

const normalizeText = (value?: string | null) => (value || '').toLowerCase();

const isExternalUrl = (value?: string | null) => Boolean(value && /^(https?:)?\/\//i.test(value));

const resolveGalleryItemUrl = (item: OwnerProjectGalleryImageDto | OwnerProjectDocumentDto) => {
  const maybeGallery = item as OwnerProjectGalleryImageDto;
  return resolveDynamicImageUrl(maybeGallery.url || item.file_path);
};

const normalizeGalleryItem = (item: OwnerProjectGalleryImageDto | string, index: number) => {
  if (typeof item === 'string') {
    return {
      name: `Foto proyek ${index + 1}`,
      url: item,
      file_path: item,
      file_type: null,
      file_size: null,
      file_size_label: null,
      document_category: 'location_photo',
    };
  }

  return item;
};

const normalizeDocumentItem = (item: OwnerProjectDocumentDto | string | Record<string, unknown>, index: number) => {
  if (typeof item === 'string') {
    return {
      name: `Dokumen pendukung ${index + 1}`,
      url: item,
      file_path: item,
      file_type: null,
      file_size: null,
      file_size_label: null,
      document_category: 'other',
    };
  }

  const raw = item as Record<string, unknown>;
  const path = String(raw.file_path || raw.path || raw.url || raw.link || raw.source_url || '');

  if (!path) {
    return null;
  }

  return {
    name: String(raw.name || raw.title || `Dokumen pendukung ${index + 1}`),
    url: path,
    file_path: path,
    file_type: typeof raw.file_type === 'string' ? raw.file_type : null,
    file_size: typeof raw.file_size === 'number' ? raw.file_size : null,
    file_size_label: null,
    document_category: typeof raw.document_category === 'string' ? raw.document_category : typeof raw.category === 'string' ? raw.category : 'other',
  };
};

const getDocumentsAsGallery = (opportunity: OpportunityMediaSource) =>
  (opportunity.owner_project_documents ?? opportunity.documents ?? [])
    .filter((document) => document.document_category === 'location_photo')
    .map((document) => ({
      name: document.name,
      url: document.file_path,
      file_path: document.file_path,
      file_type: document.file_type,
      file_size: document.file_size,
      document_category: document.document_category,
    }));

const getMetadataGallery = (opportunity: OpportunityMediaSource) =>
  (opportunity.metadata?.gallery_images ?? [])
    .map((item, index) => normalizeGalleryItem(item, index))
    .filter((item): item is ReturnType<typeof normalizeGalleryItem> => Boolean(item))
    .map((item) => ({
      ...item,
      url: resolveDynamicImageUrl(item.url || item.file_path),
    }));

const getMetadataDocumentsAsGallery = (opportunity: OpportunityMediaSource) =>
  (opportunity.metadata?.document_links ?? [])
    .map((item, index) => normalizeDocumentItem(item, index))
    .filter((item): item is NonNullable<ReturnType<typeof normalizeDocumentItem>> => Boolean(item))
    .filter((document) => document.document_category === 'location_photo')
    .map((document) => ({
      name: document.name,
      url: resolveDynamicImageUrl(document.url || document.file_path),
      file_path: document.file_path,
      file_type: document.file_type,
      file_size: document.file_size,
      file_size_label: document.file_size_label,
      document_category: document.document_category,
    }));

export const isRedirectOpportunity = (opportunity?: OpportunityMediaSource | null) =>
  Boolean(
    opportunity &&
      opportunity.submission_type === 'redirect' &&
      isExternalUrl(opportunity.source_url),
  );

export const getOpportunitySourceLabel = (opportunity?: ProviderOpportunityDto | null) =>
  opportunity?.project_source_label || (isRedirectOpportunity(opportunity)
    ? opportunity?.source_name || opportunity?.owner?.email || 'LPSE Resmi'
    : [
        opportunity?.owner?.first_name,
        opportunity?.owner?.last_name,
      ]
        .filter(Boolean)
        .join(' ')
        .trim() || opportunity?.owner?.email || 'Project Owner');

export const getOpportunityCoverImages = (opportunity?: OpportunityMediaSource | null) => {
  if (!opportunity) return [fallbackDefaultCover];

  const galleryImages = opportunity.gallery_images?.length
    ? opportunity.gallery_images.map((image, index) => resolveDynamicImageUrl(normalizeGalleryItem(image, index).url || normalizeGalleryItem(image, index).file_path))
    : getMetadataGallery(opportunity).length
      ? getMetadataGallery(opportunity).map((image) => image.url)
      : getDocumentsAsGallery(opportunity).length
        ? getDocumentsAsGallery(opportunity).map((document) => resolveGalleryItemUrl(document))
        : getMetadataDocumentsAsGallery(opportunity).map((document) => document.url);

  if (galleryImages.length > 0) {
    return galleryImages;
  }

  const haystack = normalizeText([opportunity.category, opportunity.procurement_type, opportunity.title].filter(Boolean).join(' '));
  const matched = fallbackCovers.find((item) => item.keywords.some((keyword) => haystack.includes(keyword)));

  return [matched?.cover || fallbackDefaultCover];
};

export const getOpportunityPrimaryCover = (opportunity?: OpportunityMediaSource | null, seed = 0) => {
  const covers = getOpportunityCoverImages(opportunity);

  if (covers.length === 0) {
    return fallbackDefaultCover;
  }

  return covers[seed % covers.length] || fallbackDefaultCover;
};

export const getOpportunityAction = (opportunity?: OpportunityMediaSource | null, internalHref?: string) => {
  if (isRedirectOpportunity(opportunity)) {
    return {
      label: 'Buka LPSE Resmi',
      href: opportunity?.source_url as string,
      external: true,
    };
  }

  return {
    label: 'Ajukan Penawaran',
    href: internalHref || '#',
    external: false,
  };
};
